import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { DEFAULT_PRIORITIES as PRIORITIES, priorityColor, statusColor } from "@/lib/api";
import { useIncidents } from "@/contexts/IncidentsContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Radio, History, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function IncidentDetailDialog({ incident, open, onOpenChange }) {
  const { updateIncident, pageIncident } = useIncidents();
  const { callTypes } = useSettings();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [pageBusy, setPageBusy] = useState(false);
  const [updateMsg, setUpdateMsg] = useState("");
  const [geoBusy, setGeoBusy] = useState(false);
  // Snapshot of the location as it was when the dialog opened — used to
  // detect whether the dispatcher changed the address/city/state/zip so
  // we can auto-fill the update line with "LOCATION UPDATED" if blank.
  const [origLoc, setOrigLoc] = useState({ address: "", city: "", state: "", zip_code: "" });

  useEffect(() => {
    if (incident) {
      // Pre-fill the notes field with `U/D: ` so pagers don't have to type the
      // update marker. If the current notes already start with U/D we leave
      // them; otherwise we start a fresh update line.
      const existing = (incident.notes || "").trim();
      const startsWithUD = /^U\/D:?\s?/i.test(existing);
      const preNotes = startsWithUD ? existing : "U/D: ";
      setForm({
        call_type: incident.call_type,
        priority: incident.priority,
        box_number: incident.box_number || "",
        address: incident.address || "",
        city: incident.city || "",
        state: incident.state || "",
        zip_code: incident.zip_code || "",
        notes: preNotes,
      });
      setOrigLoc({
        address: incident.address || "",
        city: incident.city || "",
        state: incident.state || "",
        zip_code: incident.zip_code || "",
      });
      setUpdateMsg("");
    }
  }, [incident]);

  if (!incident) return null;

  const save = async () => {
    setBusy(true);
    try {
      // Ensure notes carry the U/D: marker so the format is consistent regardless
      // of what the user typed.
      const typed = (form.notes || "").trim();
      const stamped = /^U\/D:?\s?/i.test(typed) ? typed : `U/D: ${typed}`;

      // Detect whether address/city/state/zip changed vs. the values loaded
      // when the dialog opened.
      const locChanged =
        (form.address || "").trim() !== (origLoc.address || "").trim() ||
        (form.city || "").trim() !== (origLoc.city || "").trim() ||
        (form.state || "").trim() !== (origLoc.state || "").trim() ||
        (form.zip_code || "").trim() !== (origLoc.zip_code || "").trim();

      // Auto-fill: if the location changed AND the dispatcher didn't type an
      // update message, send "LOCATION UPDATED" so the page still carries a
      // U/D: line. The backend prepends "U/D: " automatically.
      const finalUpdateMsg = updateMsg && updateMsg.trim()
        ? updateMsg
        : (locChanged ? "LOCATION UPDATED" : null);

      await updateIncident(incident.id, {
        call_type: form.call_type,
        priority: form.priority,
        box_number: (form.box_number || "").trim() || null,
        address: form.address,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        lat: form.lat != null ? form.lat : undefined,
        lon: form.lon != null ? form.lon : undefined,
        notes: stamped,
        update_message: finalUpdateMsg,
        repage: true,
      });
      // Reflect the stamped value back so the user sees the prefix immediately.
      setForm((f) => ({ ...f, notes: stamped }));
      toast.success(locChanged && finalUpdateMsg === "LOCATION UPDATED"
        ? "Incident re-paged as LOCATION UPDATED"
        : "Incident updated and re-paged");
      setUpdateMsg("");
      onOpenChange?.(false);
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const justPage = async () => {
    setPageBusy(true);
    try {
      await pageIncident(incident.id);
      toast.success("Page sent");
    } catch { toast.error("Page failed"); }
    finally { setPageBusy(false); }
  };

  // Re-geocode the address currently typed in the form and stamp the resulting
  // lat/lon into the form so the map pin will move when the dispatcher hits
  // UPDATE. Prefers a full "address, city, state zip" query for accuracy.
  const regeocode = async () => {
    const parts = [form.address, form.city, form.state, form.zip_code].map((s) => (s || "").trim()).filter(Boolean);
    const q = parts.join(", ");
    if (q.length < 3) { toast.error("Enter an address first"); return; }
    setGeoBusy(true);
    try {
      const { data } = await api.get("/geocode/search", { params: { q, limit: 1 } });
      const hit = Array.isArray(data) ? data[0] : null;
      if (!hit || hit.lat == null || hit.lon == null) {
        toast.error("Google couldn't locate that address");
        return;
      }
      setForm((f) => ({ ...f, lat: hit.lat, lon: hit.lon }));
      toast.success(`Pin will move to ${hit.lat.toFixed(5)}, ${hit.lon.toFixed(5)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geocoding failed");
    } finally { setGeoBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="incident-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-mono tracking-widest text-xl flex items-center gap-3">
                {incident.incident_number}
                <Badge style={{ background: priorityColor(incident.priority), color: "white" }} className="uppercase">
                  {incident.priority}
                </Badge>
                <Badge variant="outline" style={{ borderColor: statusColor(incident.status), color: statusColor(incident.status) }}>
                  {incident.status}
                </Badge>
              </DialogTitle>
              <DialogDescription className="mt-1">{incident.call_type} • {incident.address}, {incident.city}, {incident.state} {incident.zip_code}</DialogDescription>
            </div>
            <Button onClick={justPage} disabled={pageBusy} variant="destructive" data-testid="repage-btn" className="font-mono tracking-widest">
              {pageBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Radio className="w-4 h-4 mr-1" />}
              PAGE NOW
            </Button>
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 font-mono text-xs">
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground">RECEIVED</div>
                <div>{fmtTime(incident.created_at)}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground">LAST PAGED</div>
                <div>{fmtTime(incident.last_paged_at)}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground">PAGES</div>
                <div>{incident.page_count || 1}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground">CREATED BY</div>
                <div>{incident.created_by_name || "—"}</div>
              </div>
              <div className="border border-border p-2 rounded-sm col-span-2">
                <div className="text-muted-foreground">REPORTING PARTY</div>
                <div>{incident.reporting_party || "—"}</div>
              </div>
            </div>

            <Separator />

            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Call Type</Label>
              <Select value={form.call_type} onValueChange={(v) => setForm({ ...form, call_type: v })}>
                <SelectTrigger data-testid="detail-calltype-select"><SelectValue placeholder="Select call type" /></SelectTrigger>
                <SelectContent>
                  {callTypes.map((c) => <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Box Number {incident.branch === "FIRE" ? "" : "(optional)"}</Label>
              <Input
                data-testid="detail-box-number-input"
                value={form.box_number || ""}
                onChange={(e) => setForm({ ...form, box_number: e.target.value })}
                placeholder="e.g. 75-1234"
                className="font-mono"
              />
            </div>
            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-mono text-primary tracking-widest">// LOCATION</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={regeocode}
                  disabled={geoBusy}
                  data-testid="detail-regeocode-btn"
                  className="h-7 text-[11px]"
                  title="Look up the new address on Google and move the map pin when you hit UPDATE"
                >
                  {geoBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MapPin className="w-3 h-3 mr-1" />}
                  Re-Geocode & Move Pin
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mb-2">
                Changing any of these fields with a blank Update Message will auto-page as <span className="font-mono">U/D: LOCATION UPDATED</span>.
                {form.lat != null && form.lon != null && (
                  <span className="ml-2 font-mono text-primary">pin → {Number(form.lat).toFixed(5)}, {Number(form.lon).toFixed(5)}</span>
                )}
              </div>
              <Input
                data-testid="detail-address-input"
                value={form.address || ""}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Address"
                className="font-mono mb-2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  data-testid="detail-city-input"
                  value={form.city || ""}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="City"
                  className="font-mono"
                />
                <Input
                  data-testid="detail-state-input"
                  value={form.state || ""}
                  onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                  placeholder="ST"
                  maxLength={2}
                  className="font-mono uppercase"
                />
                <Input
                  data-testid="detail-zip-input"
                  value={form.zip_code || ""}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                  placeholder="ZIP"
                  className="font-mono"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="detail-notes-input" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div>
              <Label>Update Message (optional)</Label>
              <Textarea data-testid="detail-update-msg-input" rows={2} value={updateMsg} onChange={(e) => setUpdateMsg(e.target.value)} placeholder="e.g. Subject now in custody; clearing scene." />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={busy}
                variant="destructive"
                data-testid="update-incident-btn"
                className="flex-1 font-mono tracking-widest"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                UPDATE
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-sm p-4">
            <div className="font-mono text-xs text-primary mb-3 flex items-center gap-2">
              <History className="w-4 h-4" /> AUDIT LOG / UPDATE HISTORY
            </div>
            <ScrollArea className="h-[500px] pr-2">
              <div className="space-y-3">
                {(incident.updates || [])
                  .filter((u) => ["notes_changed", "note", "call_type_changed"].includes(u.type))
                  .slice().reverse().map((u, idx) => (
                  <div key={u.id || `update-${idx}`} className="border-l-2 border-primary/40 pl-3 py-1">
                    <div className="font-mono text-[10px] text-muted-foreground">{fmtTime(u.timestamp)} • {u.user}</div>
                    <div className="text-sm">
                      <Badge variant="outline" className="mr-2 font-mono text-[10px] uppercase">{u.type.replace("_", " ")}</Badge>
                      {u.message}
                    </div>
                  </div>
                ))}
                {!(incident.updates || []).length && <div className="text-muted-foreground text-xs">No updates yet.</div>}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
